import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { toast } from 'react-toastify';

import { useAuth } from '../../contexts/AuthContext';
import { usePublicLanguage } from '../../contexts/PublicLanguageContext';
import brandLogo from '../../assets/brand/logo.png';

const VerifyEmailPage = () => {
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const location = useLocation();
  const { verifyEmail } = useAuth();
  const { t } = usePublicLanguage();
  const inFlightRef = useRef(new Set());
  const toastSuccessIdRef = useRef(null);
  const toastErrorIdRef = useRef(null);

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token');
  }, [location.search]);

  useEffect(() => {
    const verifyUserEmail = async () => {
      // Starting verification with token

      if (!token) {
        // No token found
        setStatus('error');
        setMessage(t('auth.verification.errors.missingToken'));
        return;
      }

      if (inFlightRef.current.has(token)) {
        return;
      }

      inFlightRef.current.add(token);

      const storageKey = `verify_email_${token}`;
      try {
        const cached = sessionStorage.getItem(storageKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed?.status) {
            setStatus(parsed.status);
            setMessage(parsed.message || '');
            return;
          }
        }
      } catch (error) {
        // Ignore session storage failures and proceed with verification
      }

      try {
        // Calling verifyEmail API
        const result = await verifyEmail(token);
        // API result received
        
        if (result.success) {
          // Setting success status
          setStatus('success');
          setMessage(result.message);
          try {
            sessionStorage.setItem(
              storageKey,
              JSON.stringify({ status: 'success', message: result.message || '' })
            );
          } catch (error) {
            // Ignore session storage failures
          }
          const toastId = toastSuccessIdRef.current || `verify-email-success-${token}`;
          toastSuccessIdRef.current = toastId;
          toast.success(t('auth.verification.messages.success'), { toastId });
        } else {
          // Setting error status
          setStatus('error');
          setMessage(result.error);
          try {
            sessionStorage.setItem(
              storageKey,
              JSON.stringify({ status: 'error', message: result.error || '' })
            );
          } catch (error) {
            // Ignore session storage failures
          }
          const toastId = toastErrorIdRef.current || `verify-email-error-${token}`;
          toastErrorIdRef.current = toastId;
          toast.error(result.error, { toastId });
        }
      } catch (error) {
        // Caught exception during verification
        setStatus('error');
        setMessage(t('auth.verification.errors.verificationFailed'));
        try {
          sessionStorage.setItem(
            storageKey,
            JSON.stringify({
              status: 'error',
              message: t('auth.verification.errors.verificationFailed')
            })
          );
        } catch (error) {
          // Ignore session storage failures
        }
        const toastId = toastErrorIdRef.current || `verify-email-error-${token}`;
        toastErrorIdRef.current = toastId;
        toast.error(t('auth.verification.errors.verificationFailed'), { toastId });
      } finally {
        inFlightRef.current.delete(token);
      }
    };

    verifyUserEmail();
  }, [token, verifyEmail, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8"
      >
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center mb-6">
            <img
              src={brandLogo}
              alt="Candio logo"
              className="h-14 w-14 object-contain"
            />
          </Link>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t('auth.verification.title')}
          </h2>
        </div>

        {/* Status Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="card-content text-center">
            {status === 'loading' && (
              <>
                <Loader className="h-16 w-16 text-primary-600 mx-auto mb-4 animate-spin" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t('auth.verification.states.verifying')}
                </h3>
                <p className="text-gray-600">
                  {t('auth.verification.states.pleasewait')}
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t('auth.verification.states.success')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {message || t('auth.verification.states.accountActivated')}
                </p>
                <Link
                  to="/login"
                  className="btn-primary"
                >
                  {t('auth.login.loginButton')}
                </Link>
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t('auth.verification.states.error')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {message || t('auth.verification.states.invalidLink')}
                </p>
                <div className="space-y-3">
                  <Link
                    to="/signup"
                    className="btn-primary block"
                  >
                    {t('auth.verification.actions.createNewAccount')}
                  </Link>
                  <Link
                    to="/login"
                    className="btn-secondary block"
                  >
                    {t('auth.verification.actions.tryLogin')}
                  </Link>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Help */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <p className="text-sm text-gray-600">
            {t('auth.verification.help.needHelp')}{' '}
            <Link to="/contact" className="text-primary-600 hover:text-primary-500">
              {t('auth.verification.help.contactUs')}
            </Link>
          </p>
          <Link 
            to="/" 
            className="block mt-2 text-sm text-gray-500 hover:text-gray-700"
          >
            ← {t('auth.messages.backToHome')}
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
